import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useParams } from 'react-router-dom';
import { handleFilesSelected } from '../../utils/fileUtils';
import styles from './Chat.module.scss';
import { Message } from './components/Message/Message';
import { MessagesController } from './components/MessageController/MessageController';
import { BlockWrapperWithSpinner, NoContent, Spinner } from 'components';
import { appActions } from 'store/app/slice';
import { selectCurrentUser } from 'store/auth/selectors';
import {
  selectCurrentPatientCase,
  selectLastReadMessageId,
  selectPatientMessages,
  selectPatientMessagesFilters,
  selectPatientMessagesInfo,
  selectPatientMessagesIsLoading,
  selectShouldMessagesPageUpdate,
} from 'store/patients/selectors';
import { patientsActions } from 'store/patients/slice';
import { getUsers, selectOnlineUsers } from 'store/users/selectors';
import { useDispatch, useSelector } from 'store/utils';
import { TreatmentPlanTask, UserRole } from 'types';

type Props = {
  setCurrentTab(): void;
};

export const Chat = ({ setCurrentTab }: Props) => {
  const chatRef = useRef<HTMLDivElement>(null);
  const lastMessageRef = useRef<HTMLDivElement>(null);
  const offsetRef = useRef<number | undefined>();

  const lastReadMessageId = useSelector(selectLastReadMessageId);

  const currentPatientCase = useSelector(selectCurrentPatientCase);
  const patientMessageInfo = useSelector(selectPatientMessagesInfo);
  const patientMessageFilters = useSelector(selectPatientMessagesFilters);
  const messages = useSelector(selectPatientMessages);
  const isLoading = useSelector(selectPatientMessagesIsLoading);
  const shouldMessagesUpdate = useSelector(selectShouldMessagesPageUpdate);

  const dispatch = useDispatch();

  const { patientId } = useParams();

  const patientCaseID = patientId ? Number(patientId) : null;

  const currentUser = useSelector(selectCurrentUser);
  const onlineUsers = useSelector(selectOnlineUsers);

  const observer = useRef<IntersectionObserver>();

  const [canFetchNextPage, setCanFetchNextPage] = useState(false);

  const topElementRef = useCallback(
    (node: HTMLDivElement) => {
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && !patientMessageInfo?.last && canFetchNextPage) {
            setCanFetchNextPage(false);
            dispatch(
              patientsActions.setPatientMessageFilters({
                ...patientMessageFilters,
                page: patientMessageFilters.page ? patientMessageFilters.page + 1 : 1,
              }),
            );
          }
        },
        { threshold: 1.0 },
      );

      if (node) observer.current.observe(node);
    },
    [dispatch, patientMessageFilters, canFetchNextPage, patientMessageInfo?.last],
  );

  useEffect(() => {
    if (patientCaseID) {
      dispatch(
        patientsActions.getPatientMessages({
          patientCaseId: patientCaseID,
          patientMessageParams: patientMessageFilters,
        }),
      );
    }
  }, [patientCaseID, patientMessageFilters, dispatch]);

  useLayoutEffect(() => {
    if (!shouldMessagesUpdate) {
      dispatch(patientsActions.setShouldMessagesPageUpdate(true));
      return;
    }
    if (chatRef.current && patientMessageInfo?.first) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
      return;
    }
    if (chatRef.current) {
      const scrollHeight = chatRef.current.scrollHeight;
      offsetRef.current &&
        chatRef.current.scrollTo({
          top: scrollHeight - offsetRef.current,
        });
    }
  }, [messages, patientMessageInfo?.first]);

  useEffect(() => {
    messages.length > 0 && setCanFetchNextPage(true);
  }, [messages]);

  const { id, role } = useSelector(selectCurrentUser) ?? {};

  useEffect(() => {
    return () => {
      offsetRef.current = chatRef?.current?.scrollHeight;
    };
  }, [messages]);

  useEffect(() => {
    return () => {
      dispatch(patientsActions.resetPatientMessages());
    };
  }, [dispatch]);

  useEffect(() => {
    if (
      currentPatientCase?.assignedDoctorId &&
      messages.length &&
      patientCaseID &&
      !messages[0].isRead &&
      messages[0].createdBy !== id &&
      lastReadMessageId !== messages[0].id &&
      (currentPatientCase?.clinicId === id || currentPatientCase?.assignedDoctorId === id)
    ) {
      dispatch(patientsActions.readPatientMessage(patientCaseID));
      dispatch(patientsActions.setLastReadMessage(messages[0].id));
    }
  }, [messages, currentPatientCase?.assignedDoctorId]);

  useEffect(() => {
    dispatch(appActions.setIsModalOpened({ name: 'messagingErrorModal', isOpened: false }));
    dispatch(appActions.setIsModalOpened({ name: 'attachmentErrorModal', isOpened: false }));
  }, []);

  const users = useSelector(getUsers);
  const { name: clinicName, id: clinicId } = users.find((clinic) => clinic.id === currentPatientCase?.clinicId) ?? {};
  const { name: doctorName, id: doctorId } =
    users.find((doctor) => doctor.id === currentPatientCase?.assignedDoctorId) ?? {};

  const isEnabled =
    role !== UserRole.ADMIN &&
    (id === currentPatientCase?.clinicId || id === currentPatientCase?.assignedDoctorId) &&
    currentPatientCase?.task !== TreatmentPlanTask.TO_SUBMIT;

  const isCaseBelongsToUser = currentPatientCase?.clinicId === id || currentPatientCase?.assignedDoctorId === id;

  const isClinic = clinicId === id;

  const { getRootProps } = useDropzone({
    noClick: true,
    onDrop: (plainFiles) => isEnabled && handleFilesSelected(plainFiles, patientCaseID!, dispatch),
  });

  const showNextPageLoader = isLoading && messages.length;

  if (!messages.length && isLoading)
    return (
      <div className={styles.container}>
        <BlockWrapperWithSpinner transparent isLoading />
      </div>
    );

  const reversedMessages = [...messages].reverse();

  return (
    <div
      {...getRootProps({
        className: styles.container,
      })}>
      <div className={styles.messages} ref={chatRef}>
        {showNextPageLoader && (
          <div className={styles.nextPageSpinnerContainer}>
            <Spinner isLoading />
          </div>
        )}
        {canFetchNextPage && <div ref={topElementRef} className={styles.nextPageTrigger} />}
        {messages.length ? (
          reversedMessages.map((el, index) => {
            const sender =
              el.createdBy === clinicId
                ? clinicName
                : el.createdBy === doctorId
                  ? doctorName
                  : users.find((u) => u.id === el.createdBy)?.name;
            return (
              <Message
                key={el.id}
                message={el}
                position={
                  isClinic && el.createdBy === id ? 'right' : el.createdBy !== clinicId && !isClinic ? 'right' : 'left'
                }
                name={sender}
                role={el.createdBy === clinicId ? 'Studio' : 'Doctor'}
                ref={index === 0 ? lastMessageRef : null}
                onlineStatus={
                  el.createdBy === currentUser?.id ? 'off' : onlineUsers.includes(el.createdBy) ? 'online' : 'offline'
                }
                isEnabled={isEnabled}
                currentUserId={id}
                isParticipantOfConversation={isCaseBelongsToUser}
              />
            );
          })
        ) : (
          <NoContent pageKey="discussion" className={styles.noContent} />
        )}
      </div>
      <MessagesController isEnabled={isEnabled} patientCaseID={patientCaseID} setCurrentTab={setCurrentTab} />
    </div>
  );
};
